// Copied from @confluence/ai-common/ui AIErrorText and its wrapper AIPageErrorText
import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import Link from '@atlaskit/link/link';
import { Box, type BoxProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';
import {
	AIErrorLinkType,
	type AIErrorType,
	ErrorMessageKeyErrorLinkMap,
	ErrorTypeErrorMessageMap,
} from './types';

const textStyles = cssMap({
	root: {
		color: token('color.text.subtlest'),
	},
});

export const TROUBLE_SHOOTING_DOCS_URL =
	'https://support.atlassian.com/organization-administration/docs/troubleshooting-for-atlassian-intelligence/';
export const USAGE_LIMITS_DOCS_URL =
	'https://support.atlassian.com/organization-administration/docs/excessive-use-in-atlassian-intelligence/';
export const ACCEPTABLE_USE_POLICY_URL = 'https://www.atlassian.com/legal/acceptable-use-policy';

const LINK_MAP: { [key: string]: string } = {
	[AIErrorLinkType.RateLimit]: USAGE_LIMITS_DOCS_URL,
	[AIErrorLinkType.AupViolation]: ACCEPTABLE_USE_POLICY_URL,
	[AIErrorLinkType.Troubleshooting]: TROUBLE_SHOOTING_DOCS_URL,
};

type AIErrorTextProps = BoxProps<'p'> & {
	analyticsAttributes?: Record<string, any>;
	errorType: AIErrorType;
	source: string;
};

export const AIErrorText = ({
	errorType,
	source,
	analyticsAttributes,
	xcss,
	...props
}: AIErrorTextProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const errorMessageKey = ErrorTypeErrorMessageMap[errorType];
	const errorMessage = messages[errorMessageKey];

	const linkType = AIErrorLinkType[ErrorMessageKeyErrorLinkMap[errorMessageKey]];

	const onDocsLinkClicked = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source,
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: `atlassianIntelligence${linkType}DocsLink`,
				...(analyticsAttributes ? { attributes: analyticsAttributes } : {}),
			},
		}).fire();
	};

	// This is safe for messages without link replacements also.
	// formatMessage ignores replacements when none exist in the string
	const resolvedMessage = formatMessage(errorMessage, {
		a: (chunks: ReactNode[]) => (
			<Link href={LINK_MAP[linkType]} target="_blank" onClick={onDocsLinkClicked}>
				{chunks}
			</Link>
		),
	});

	return (
		<Box xcss={xcss || textStyles.root} {...props}>
			{resolvedMessage}
		</Box>
	);
};
